import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { CaretLeft, CaretRight, Tray } from "@phosphor-icons/react";
import {
  fetchInboxItems,
  selectInboxCategoryCounts,
  selectInboxFilters,
  selectInboxItems,
  selectInboxListError,
  selectInboxListStatus,
  selectInboxPagination,
} from "../../../slice/InboxSlice";
import InboxListItem from "./InboxListItem";
import InboxFolderFilter from "./InboxFolderFilter";
import InboxMatterSearch, { matterSearchLabel } from "./InboxMatterSearch";
import {
  InboxFilterChip,
  InboxFiltersRow,
  InboxListBody,
  InboxListCount,
  InboxListEmpty,
  InboxListEmptyDescription,
  InboxListEmptyTitle,
  InboxListErrorBanner,
  InboxListHeader,
  InboxListPane,
  InboxListTitle,
  InboxListTitleRow,
  InboxPaginationButton,
  InboxPaginationFooter,
  InboxPaginationPageForm,
  InboxPaginationPageInput,
  InboxPaginationPageLabel,
  InboxSelectionActions,
  InboxSelectionBar,
  InboxSelectionButton,
  InboxSkeletonBlock,
  InboxSkeletonRow,
} from "../InboxStyled";

/**
 * Left pane of the Inbox 3-pane layout.
 *
 * Owns:
 *   - Header (title, server-side total count, matter search, filter chips)
 *   - Body (one row per inbox item — see `InboxListItem`)
 *   - Pagination footer (page-number, prev/next)
 *
 * Reads list state from `InboxSlice`. Dispatches `fetchInboxItems` for
 * pagination + filter changes. The page mount itself owns the *initial*
 * fetch (so we can also seed the unread-count thunk on the same tick) —
 * this component is purely a renderer + paginator/filterer.
 *
 * Selected-row highlighting is purely URL-driven via `selectedDocumentId`
 * (mirrored from `useParams` in the page). We don't track "selected"
 * locally so cross-component reads (right rail header, viewer) all stay
 * in sync without a second source of truth.
 */
const SkeletonRows = () => (
  <>
    {Array.from({ length: 8 }).map((_, idx) => (
      <InboxSkeletonRow key={idx}>
        <InboxSkeletonBlock $w="80%" $h={14} />
        <InboxSkeletonBlock $w="100%" $h={11} />
        <InboxSkeletonBlock $w="50%" $h={10} />
      </InboxSkeletonRow>
    ))}
  </>
);

const InboxList = ({
  selectedDocumentId = null,
  onSelectDocument,
  checkedIds = [],
  onMarkSelectionReviewed,
  onClearChecked,
}) => {
  const dispatch = useDispatch();
  const items = useSelector(selectInboxItems);
  const status = useSelector(selectInboxListStatus);
  const error = useSelector(selectInboxListError);
  const pagination = useSelector(selectInboxPagination);
  const filters = useSelector(selectInboxFilters);
  const categoryCounts = useSelector(selectInboxCategoryCounts);
  const [selectedMatter, setSelectedMatter] = useState(null);

  const listArgs = useCallback(
    (overrides = {}) => ({
      matterId: filters.matterId,
      includeSnoozed: filters.includeSnoozed,
      recentlyReviewed: filters.recentlyReviewed,
      category: filters.category,
      page: pagination.page,
      pageSize: pagination.pageSize,
      ...overrides,
    }),
    [filters, pagination]
  );

  const handleSelectMatter = useCallback(
    (matter) => {
      setSelectedMatter({
        id: matter.id,
        label: matterSearchLabel(matter),
      });
      dispatch(fetchInboxItems(listArgs({ matterId: matter.id, page: 1 })));
    },
    [dispatch, listArgs]
  );

  const handleClearMatter = useCallback(() => {
    setSelectedMatter(null);
    dispatch(fetchInboxItems(listArgs({ matterId: null, page: 1 })));
  }, [dispatch, listArgs]);

  const handleToggleSnoozed = useCallback(() => {
    dispatch(
      fetchInboxItems(
        listArgs({
          includeSnoozed: !filters.includeSnoozed,
          page: 1,
        })
      )
    );
  }, [dispatch, filters.includeSnoozed, listArgs]);

  const handleToggleReviewed = useCallback(() => {
    dispatch(
      fetchInboxItems(
        listArgs({
          recentlyReviewed: !filters.recentlyReviewed,
          page: 1,
        })
      )
    );
  }, [dispatch, filters.recentlyReviewed, listArgs]);

  const handleSelectFolder = useCallback(
    (nextCategory) => {
      dispatch(fetchInboxItems(listArgs({ category: nextCategory, page: 1 })));
    },
    [dispatch, listArgs]
  );

  const handlePrev = useCallback(() => {
    if (!pagination.hasPrevious || pagination.page <= 1) return;
    dispatch(fetchInboxItems(listArgs({ page: pagination.page - 1 })));
  }, [dispatch, listArgs, pagination]);

  const handleNext = useCallback(() => {
    if (!pagination.hasNext) return;
    dispatch(fetchInboxItems(listArgs({ page: pagination.page + 1 })));
  }, [dispatch, listArgs, pagination]);

  const handleRetry = useCallback(() => {
    dispatch(fetchInboxItems(listArgs()));
  }, [dispatch, listArgs]);

  const handleGoToPage = useCallback(
    (targetPage) => {
      const clamped = Math.min(
        Math.max(targetPage, 1),
        pagination.totalPages
      );
      if (Number.isNaN(clamped) || clamped === pagination.page) return;
      dispatch(fetchInboxItems(listArgs({ page: clamped })));
    },
    [dispatch, listArgs, pagination]
  );

  // "Page X of Y" doubles as a jump-to-page control: clicking it swaps the
  // label for a small number input (Enter/blur commits, Escape cancels).
  const [isEditingPage, setIsEditingPage] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const pageInputRef = useRef(null);

  useEffect(() => {
    if (isEditingPage && pageInputRef.current) {
      pageInputRef.current.focus();
      pageInputRef.current.select();
    }
  }, [isEditingPage]);

  const handlePageLabelClick = useCallback(() => {
    setPageInputValue(String(pagination.page));
    setIsEditingPage(true);
  }, [pagination.page]);

  const commitPageInput = useCallback(() => {
    const parsed = parseInt(pageInputValue, 10);
    if (!Number.isNaN(parsed)) {
      handleGoToPage(parsed);
    }
    setIsEditingPage(false);
  }, [pageInputValue, handleGoToPage]);

  const handlePageInputKeyDown = useCallback((event) => {
    if (event.key === "Escape") {
      setIsEditingPage(false);
    }
  }, []);

  // First-mount skeleton — keep showing real rows on subsequent re-fetches
  // (page change, filter toggle) so the page doesn't flash a skeleton on
  // every navigation. Status === "loading" + items.length === 0 means
  // we genuinely have nothing to show yet.
  const isInitialLoading =
    status === "loading" && (!items || items.length === 0);

  return (
    <InboxListPane aria-label="Attorney inbox list">
      <InboxListHeader>
        <InboxListTitleRow>
          <InboxListTitle>Inbox</InboxListTitle>
          {pagination.count > 0 && (
            <InboxListCount aria-live="polite">
              {pagination.count.toLocaleString()} item
              {pagination.count === 1 ? "" : "s"}
            </InboxListCount>
          )}
        </InboxListTitleRow>
        <InboxMatterSearch
          selectedMatterId={selectedMatter?.id ?? filters.matterId}
          onSelect={handleSelectMatter}
          onClear={handleClearMatter}
        />
        <InboxFolderFilter
          category={filters.category}
          counts={categoryCounts}
          onSelect={handleSelectFolder}
        />
        <InboxFiltersRow>
          <InboxFilterChip
            type="button"
            $active={filters.includeSnoozed}
            onClick={handleToggleSnoozed}
            aria-pressed={filters.includeSnoozed}
          >
            {filters.includeSnoozed ? "Showing snoozed" : "Show snoozed"}
          </InboxFilterChip>
          <InboxFilterChip
            type="button"
            $active={filters.recentlyReviewed}
            onClick={handleToggleReviewed}
            aria-pressed={filters.recentlyReviewed}
            title="Mail you marked reviewed in the past two weeks, most recent first"
          >
            Recently reviewed
          </InboxFilterChip>
        </InboxFiltersRow>
      </InboxListHeader>

      {error && (
        <InboxListErrorBanner role="alert">
          {error}{" "}
          <button
            type="button"
            onClick={handleRetry}
            style={{
              background: "none",
              border: "none",
              color: "#A61412",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Retry
          </button>
        </InboxListErrorBanner>
      )}

      {checkedIds.length > 1 && (
        <InboxSelectionBar>
          <span>
            {checkedIds.length} selected
          </span>
          <InboxSelectionActions>
            <InboxSelectionButton
              type="button"
              onClick={onMarkSelectionReviewed}
            >
              Mark reviewed
            </InboxSelectionButton>
            <InboxSelectionButton type="button" onClick={onClearChecked}>
              Clear
            </InboxSelectionButton>
          </InboxSelectionActions>
        </InboxSelectionBar>
      )}

      <InboxListBody>
        {isInitialLoading ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <InboxListEmpty role="status">
            <Tray size={32} weight="duotone" color="#7688A3" />
            <InboxListEmptyTitle>
              {filters.recentlyReviewed
                ? "Nothing reviewed lately"
                : "Your inbox is clear"}
            </InboxListEmptyTitle>
            <InboxListEmptyDescription>
              {filters.recentlyReviewed
                ? "Nothing marked reviewed in the past two weeks."
                : selectedMatter
                ? `Nothing in the inbox for ${selectedMatter.label}.`
                : filters.includeSnoozed
                ? "Nothing to review — including snoozed items."
                : "Nothing to review. Snoozed items aren't shown by default."}
            </InboxListEmptyDescription>
          </InboxListEmpty>
        ) : (
          items.map((item) => (
            <InboxListItem
              key={item.id}
              item={item}
              selected={
                selectedDocumentId != null &&
                // String comparison: document ids are UUIDs post
                // split-schema; Number() made this NaN === NaN.
                String(selectedDocumentId) === String(item.id)
              }
              checked={checkedIds.some(
                (id) => String(id) === String(item.id)
              )}
              onSelect={onSelectDocument}
            />
          ))
        )}
      </InboxListBody>

      {pagination.totalPages > 1 && (
        <InboxPaginationFooter>
          {isEditingPage ? (
            <InboxPaginationPageForm
              onSubmit={(event) => {
                event.preventDefault();
                commitPageInput();
              }}
            >
              Page{" "}
              <InboxPaginationPageInput
                ref={pageInputRef}
                type="number"
                inputMode="numeric"
                min={1}
                max={pagination.totalPages}
                value={pageInputValue}
                onChange={(event) => setPageInputValue(event.target.value)}
                onKeyDown={handlePageInputKeyDown}
                onBlur={commitPageInput}
                aria-label="Go to page"
              />{" "}
              of <strong>{pagination.totalPages}</strong>
            </InboxPaginationPageForm>
          ) : (
            <InboxPaginationPageLabel
              type="button"
              onClick={handlePageLabelClick}
              aria-label="Jump to a specific page"
            >
              Page <strong>{pagination.page}</strong> of{" "}
              <strong>{pagination.totalPages}</strong>
            </InboxPaginationPageLabel>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <InboxPaginationButton
              type="button"
              onClick={handlePrev}
              disabled={!pagination.hasPrevious}
              aria-label="Previous page"
            >
              <CaretLeft size={12} weight="bold" />
              Prev
            </InboxPaginationButton>
            <InboxPaginationButton
              type="button"
              onClick={handleNext}
              disabled={!pagination.hasNext}
              aria-label="Next page"
            >
              Next
              <CaretRight size={12} weight="bold" />
            </InboxPaginationButton>
          </div>
        </InboxPaginationFooter>
      )}
    </InboxListPane>
  );
};

InboxList.propTypes = {
  selectedDocumentId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSelectDocument: PropTypes.func.isRequired,
  checkedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onMarkSelectionReviewed: PropTypes.func,
  onClearChecked: PropTypes.func,
};

export default InboxList;
